import { useEffect, useRef } from 'react';
import { useGmailConnectionStatus } from './useGmailConnectionStatus';
import { householdService } from '@/services';

/**
 * Silently heals a Gmail connection that is nearing or just past its 7-day
 * watch expiry.
 *
 * The watch is renewed autonomously server-side (a daily cron re-arms watches
 * within 2 days of expiry), so a `expiring_soon` / `expired` state usually
 * means "the watch will be — or could have been — renewed without the user
 * doing anything." This hook proactively triggers the server-side soft
 * refresh, which re-arms the watch using the stored refresh_token WITHOUT
 * sending the user through Google consent. On success it refetches so the
 * banner never surfaces; only when the token is genuinely dead (the server
 * answers `needs_oauth`) does the reconnect banner remain.
 *
 * Fire-and-forget, at most once per household per mount. No-ops for healthy
 * (`connected`), `revoked`, and `not_connected` states — those either need
 * nothing or require a full OAuth round-trip the user must initiate.
 */
export function useGmailAutoHeal(householdId: string | null | undefined): void {
  const { data, refetch } = useGmailConnectionStatus(householdId);
  // Guards against re-firing on every render while the state stays healable.
  const attemptedFor = useRef<string | null | undefined>(null);

  const state = data?.state;
  // `needs_attention` is a recoverable watch-renewal failure — the same
  // server-side soft refresh that heals expiring_soon/expired re-arms the
  // watch here too, so retrying on mount lets the banner self-clear.
  const healable =
    state === 'expiring_soon' ||
    state === 'expired' ||
    state === 'needs_attention';

  useEffect(() => {
    if (!householdId || !healable) return;
    if (attemptedFor.current === householdId) return;
    attemptedFor.current = householdId;

    void householdService
      .refreshGmailSubscription(householdId)
      .then((result) => {
        // ok → watch re-armed with the existing refresh_token; pull fresh
        // status so the connection reads as healthy again.
        if (result.ok) {
          refetch();
          return;
        }
        // Couldn't auto-heal. needs_oauth → token is dead (the critical
        // expired/revoked banner will surface and ask the user to reconnect);
        // otherwise a server / watch-arm hiccup (watch_failed). Either way the
        // outcome is otherwise invisible client-side — log it so a silent
        // heal-failure loop is diagnosable in prod.
        console.warn(
          `[useGmailAutoHeal] silent refresh did not resolve gmail connection (needsOAuth=${result.needsOAuth})`,
        );
      })
      .catch((err) => {
        // Transient network/server failure. Leave the state untouched — the
        // daily renewal cron is the backstop and we never push the user to
        // Google from this background path. Log for prod visibility.
        console.warn('[useGmailAutoHeal] silent refresh threw:', err);
      });
  }, [householdId, healable, refetch]);
}
